import { 
  SafetyAlert, 
  SafetyStats, 
  RiskZone, 
  SafetyRecommendation, 
  AlertHistoryItem,
  NotificationSettings,
  Location,
  TimeBasedRisk,
  SafetySeverity,
  SafetyStatus,
  SafetyAlertType
} from './types';

// Utility to generate a random ID
const generateId = (prefix: string): string => {
  return `${prefix}-${Math.floor(Math.random() * 10000)}`;
};

// Utility to generate a random date in the past few days
const generateRecentDate = (maxDaysAgo: number = 7): Date => {
  const now = new Date();
  const daysAgo = Math.random() * maxDaysAgo;
  return new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
};

// Utility for random selection from array
const randomChoice = <T>(arr: ReadonlyArray<T> | Array<T>): T => {
  return arr[Math.floor(Math.random() * arr.length)];
};

// Generate a realistic location
const generateLocation = (): Location => {
  const rooms = [
    'Living Room', 'Kitchen', 'Bathroom', 'Master Bedroom', 
    'Guest Bedroom', 'Hallway', 'Entryway', 'Dining Room'
  ];
  
  const zones = [
    'North Wall', 'South Wall', 'East Wall', 'West Wall', 
    'Center', 'Near Window', 'Near Door', 'Corner'
  ];
  
  return {
    room: randomChoice(rooms),
    zone: randomChoice(zones),
    coordinates: {
      x: Math.floor(Math.random() * 100),
      y: Math.floor(Math.random() * 100)
    }
  };
};

// Generate safety alerts
const generateSafetyAlerts = (count: number = 20): SafetyAlert[] => {
  const alerts: SafetyAlert[] = [];
  
  const alertTypes = ['FALL_RISK', 'WANDERING', 'ENVIRONMENTAL', 'BEHAVIORAL'] as const;
  const severities = ['HIGH', 'MEDIUM', 'LOW'] as const;
  const statuses = ['ACTIVE', 'ACKNOWLEDGED', 'RESOLVED'] as const;
  
  // Sample descriptions for each alert type
  const descriptions: Record<SafetyAlertType, string[]> = {
    FALL_RISK: [
      'Unsteady movement pattern detected in bathroom',
      'Multiple sudden motion events detected in kitchen area',
      'Significant balance shift detected while standing',
      'Irregular gait pattern after medication time',
      'Repeated grabbing motions detected near walkway'
    ],
    WANDERING: [
      'Unusual movement patterns during night hours',
      'Door sensor activated at 3 AM',
      'Circular motion patterns in unfamiliar areas',
      'Repetitive walking patterns detected',
      'Motion sensors triggered in unusual sequence'
    ],
    ENVIRONMENTAL: [
      'High humidity detected in bathroom',
      'Moisture sensor activated under kitchen sink',
      'Stove temperature sensor active without movement',
      'Low light conditions detected in hallway',
      'Moisture detected on bathroom floor'
    ],
    BEHAVIORAL: [
      'Medication cabinet sensor missed regular schedule',
      'Significant change in daily movement patterns',
      'Reduced motion detected in social areas',
      'Abnormal nighttime movement frequency',
      'Irregular meal preparation sensor activity'
    ]
  };
  
  // Sample risk factors for each alert type
  const riskFactors: Record<SafetyAlertType, string[]> = {
    FALL_RISK: [
      'Recent medication change',
      'Previous fall events detected',
      'Low lighting sensor readings',
      'Floor moisture detection events',
      'Uneven movement patterns',
      'Reduced motion stability',
      'Balance shift frequency'
    ],
    WANDERING: [
      'Nighttime door sensor activation',
      'Room transition pattern changes',
      'Sleep disruption detected',
      'Medication timing correlation',
      'Location sensor irregularities',
      'Increased aimless motion patterns',
      'Evening restlessness sensors'
    ],
    ENVIRONMENTAL: [
      'Multiple clutter sensors triggered',
      'Low ambient light readings',
      'Temperature pattern anomalies',
      'Appliance usage without presence',
      'Missing motion in critical areas',
      'Floor moisture sensor readings',
      'High humidity measurements'
    ],
    BEHAVIORAL: [
      'Cognitive assessment decline',
      'Motion patterns suggesting distress',
      'Medication timing irregularities',
      'Bathroom visit frequency change',
      'Hydration tracker readings',
      'Social room occupancy decrease',
      'Nutrition tracker anomalies'
    ]
  };
  
  // Generate unique alerts
  for (let i = 0; i < count; i++) {
    const alertType = randomChoice(alertTypes) as SafetyAlertType;
    const severity = randomChoice(severities) as SafetySeverity;
    const status = randomChoice(statuses) as SafetyStatus;
    
    // Weight active alerts and higher severities for more realistic data
    const adjustedStatus = 
      Math.random() < 0.6 
        ? (severity === 'HIGH' ? 'ACTIVE' : status) 
        : status;
    
    const timestamp = generateRecentDate();
    
    const alert: SafetyAlert = {
      id: generateId('ALT'),
      timestamp,
      type: alertType,
      severity,
      location: generateLocation(),
      description: randomChoice(descriptions[alertType]),
      status: adjustedStatus,
      riskFactors: Array(Math.floor(Math.random() * 3) + 1)
        .fill(0)
        .map(() => randomChoice(riskFactors[alertType]))
    };
    
    // Add response for acknowledged and resolved alerts
    if (alert.status !== 'ACTIVE') {
      const responseTime = new Date(timestamp.getTime() + Math.random() * 60 * 60 * 1000); // Up to 1 hour later
      
      alert.response = {
        time: responseTime,
        action: alert.status === 'ACKNOWLEDGED' 
          ? 'Alert acknowledged by caregiver' 
          : 'Issue resolved by caregiver',
        responder: 'Caregiver'
      };
      
      // Add some random caregiver notes to a few alerts
      if (Math.random() < 0.3) {
        alert.caregiverNotes = [
          {
            timestamp: new Date(responseTime.getTime() + 5 * 60 * 1000),
            content: `Followed up on ${alert.description.toLowerCase()}. Monitoring situation.`,
            author: 'Primary Caregiver'
          }
        ];
        
        // Add a second note sometimes
        if (Math.random() < 0.5) {
          alert.caregiverNotes.push({
            timestamp: new Date(responseTime.getTime() + 30 * 60 * 1000),
            content: alert.status === 'RESOLVED' 
              ? 'Implemented safety measures to prevent recurrence.' 
              : 'Will discuss with healthcare provider during next visit.',
            author: 'Primary Caregiver'
          });
        }
      }
    }
    
    alerts.push(alert);
  }
  
  return alerts;
};

// Generate risk zones
const generateRiskZones = (): RiskZone[] => {
  const riskZones: RiskZone[] = [];
  
  const riskFactors = {
    'Living Room': [
      'Motion sensors detect cluttered paths', 
      'Low ambient light readings', 
      'Rug edge detection triggers', 
      'Low furniture proximity sensors'
    ],
    'Kitchen': [
      'Floor moisture detection frequency', 
      'Stove temperature anomalies', 
      'Reach sensors for high cabinets', 
      'Hot water temperature readings'
    ],
    'Bathroom': [
      'Floor moisture detection', 
      'Grab bar usage patterns', 
      'Motion sensor blind spots', 
      'Bath mat position sensors',
      'Tub entry/exit motion patterns'
    ],
    'Master Bedroom': [
      'Nighttime motion path disruptions', 
      'Insufficient automated lighting triggers', 
      'Bed entry/exit difficulty patterns', 
      'Cable/cord proximity sensors'
    ],
    'Hallway': [
      'Motion sensor gaps', 
      'Narrow passage navigation patterns', 
      'Floor evenness sensor data', 
      'Wall contact frequency'
    ],
    'Entryway': [
      'Threshold transition difficulty', 
      'Entry lighting sensor gaps', 
      'Floor mat position sensors', 
      'Weather-based slip risk detection'
    ]
  };
  
  // Create risk zones for different rooms
  Object.entries(riskFactors).forEach(([room, factors]) => {
    const location: Location = {
      room,
      zone: randomChoice(['Center', 'Near Door', 'Corner', 'Near Window']),
      coordinates: {
        x: Math.floor(Math.random() * 100),
        y: Math.floor(Math.random() * 100)
      }
    };
    
    const riskScore = Math.floor(Math.random() * 100);
    const riskLevel: 'HIGH' | 'MEDIUM' | 'LOW' = 
      riskScore >= 70 ? 'HIGH' : 
      riskScore >= 40 ? 'MEDIUM' : 
      'LOW';
    
    const randomFactors = Array(Math.floor(Math.random() * 3) + 1)
      .fill(0)
      .map(() => randomChoice(factors));
    
    riskZones.push({
      id: generateId('RISK'),
      location,
      riskLevel,
      riskScore,
      riskFactors: randomFactors,
      alertFrequency: Math.floor(Math.random() * 10),
      recommendations: [generateId('REC'), generateId('REC')],
      lastAssessment: generateRecentDate(30),
      trend: randomChoice(['INCREASING', 'DECREASING', 'STABLE'])
    });
  });
  
  return riskZones;
};

// Generate safety recommendations
const generateSafetyRecommendations = (): SafetyRecommendation[] => {
  const recommendations: SafetyRecommendation[] = [];
  
  const recommendationsByCategory = {
    ENVIRONMENTAL: [
      {
        title: 'Install Bathroom Grab Bars',
        description: 'Install grab bars in the bathroom to provide support and stability.',
        impact: 80,
        steps: [
          'Purchase ADA-compliant grab bars',
          'Identify optimal placement near toilet and shower',
          'Install with proper anchoring to wall studs',
          'Test to ensure stability'
        ]
      },
      {
        title: 'Improve Hallway Lighting',
        description: 'Add motion-activated night lights to improve visibility during nighttime trips.',
        impact: 75,
        steps: [
          'Purchase motion-activated LED night lights',
          'Install at knee-height along hallway walls',
          'Test motion sensitivity and light coverage',
          'Consider adding light strips along baseboards'
        ]
      },
      {
        title: 'Remove Tripping Hazards',
        description: 'Remove or secure rugs, cords, and clutter from walking paths.',
        impact: 85,
        steps: [
          'Identify all loose rugs and either remove or secure with double-sided tape',
          'Organize cords and cables using cord management solutions',
          'Clear all walkways of clutter',
          'Ensure furniture is arranged to allow wide walking paths'
        ]
      }
    ],
    BEHAVIORAL: [
      {
        title: 'Establish Consistent Medication Routine',
        description: 'Create a structured daily medication schedule with reminders.',
        impact: 70,
        steps: [
          'Set up automated pill dispenser with alarms',
          'Create a medication chart showing daily schedule',
          'Set up smartphone or smart speaker reminders',
          'Establish a verification system with caregiver'
        ]
      },
      {
        title: 'Implement Regular Exercise Program',
        description: 'Schedule daily balance and strength exercises to reduce fall risk.',
        impact: 65,
        steps: [
          'Consult with physical therapist for appropriate exercises',
          'Schedule short exercise sessions 2-3 times daily',
          'Focus on leg strength and balance activities',
          'Document progress and adjust as needed'
        ]
      }
    ],
    EQUIPMENT: [
      {
        title: 'Install Smart Stove Shutoff',
        description: 'Add automatic shutoff device to stove to prevent fire hazards.',
        impact: 90,
        steps: [
          'Purchase automatic stove shutoff device',
          'Schedule professional installation',
          'Test functionality with various scenarios',
          'Set up alerts for caregivers when triggered'
        ]
      },
      {
        title: 'Provide Walking Assistance Device',
        description: 'Ensure proper walking cane or walker is available and properly fitted.',
        impact: 75,
        steps: [
          'Consult with physical therapist for appropriate device recommendation',
          'Purchase recommended device',
          'Ensure proper height adjustment',
          'Practice proper usage technique'
        ]
      }
    ],
    TRAINING: [
      {
        title: 'Safe Transfer Training',
        description: 'Train on proper technique for safely moving between surfaces.',
        impact: 80,
        steps: [
          'Schedule session with physical therapist',
          'Practice bed to chair transfers',
          'Practice sit to stand techniques',
          'Create visual reminder cards for proper techniques'
        ]
      },
      {
        title: 'Emergency Response Training',
        description: 'Practice using emergency call system and response protocols.',
        impact: 85,
        steps: [
          'Review operation of all emergency devices',
          'Practice emergency call procedure',
          'Create simple instruction cards near each device',
          'Run monthly practice drills'
        ]
      }
    ],
    MEDICAL: [
      {
        title: 'Medication Review',
        description: 'Schedule comprehensive medication review to identify fall risk factors.',
        impact: 75,
        steps: [
          'Schedule appointment with geriatric pharmacist',
          'Bring all current medications to review',
          'Discuss side effects and interaction concerns',
          'Create updated medication plan'
        ]
      },
      {
        title: 'Vision Assessment',
        description: 'Schedule eye exam to ensure prescription is current and address vision issues.',
        impact: 70,
        steps: [
          'Schedule comprehensive eye exam',
          'Assess need for updated prescription',
          'Evaluate for conditions like glaucoma or cataracts',
          'Consider contrast-enhancing lenses if appropriate'
        ]
      }
    ]
  };
  
  const statuses = ['PENDING', 'IN_PROGRESS', 'IMPLEMENTED', 'DISMISSED'] as const;
  const priorities = ['HIGH', 'MEDIUM', 'LOW'] as const;
  
  // Generate recommendations from each category
  Object.entries(recommendationsByCategory).forEach(([category, items]) => {
    items.forEach(item => {
      const priority = item.impact > 80 ? 'HIGH' : item.impact > 60 ? 'MEDIUM' : 'LOW';
      
      const createdAt = generateRecentDate(60); // Within last 60 days
      
      let status: typeof statuses[number];
      let implementedAt: Date | undefined;
      
      // Weight the randomness to have more pending high-priority items
      if (priority === 'HIGH') {
        status = Math.random() < 0.7 ? 'PENDING' : randomChoice(statuses);
      } else {
        status = randomChoice(statuses);
      }
      
      if (status === 'IMPLEMENTED') {
        implementedAt = new Date(createdAt.getTime() + Math.random() * 20 * 24 * 60 * 60 * 1000); // Up to 20 days later
      }
      
      recommendations.push({
        id: generateId('REC'),
        title: item.title,
        description: item.description,
        detailedSteps: item.steps,
        status,
        priority,
        impact: item.impact,
        targetRiskZones: [generateId('RISK')],
        createdAt,
        implementedAt,
        category: category as any,
        estimatedCost: `$${Math.floor(Math.random() * 500) + 50}`,
        estimatedTimeToImplement: `${Math.floor(Math.random() * 4) + 1} hours`
      });
    });
  });
  
  return recommendations;
};

// Generate alert history
const generateAlertHistory = (): AlertHistoryItem[] => {
  const history: AlertHistoryItem[] = [];
  
  const alertTypes = ['FALL_RISK', 'WANDERING', 'ENVIRONMENTAL', 'BEHAVIORAL'] as const;
  const severities = ['HIGH', 'MEDIUM', 'LOW'] as const;
  const locations = [
    'Living Room', 'Kitchen', 'Bathroom', 'Master Bedroom', 
    'Guest Bedroom', 'Hallway', 'Entryway', 'Dining Room'
  ];
  
  const resolutions = [
    'Adjusted environment based on sensor data',
    'Provided additional motion supports',
    'Recalibrated medication reminder system',
    'Increased sensor monitoring frequency',
    'Installed additional safety sensors',
    'Modified daily routine tracking',
    'Scheduled healthcare follow-up based on data',
    'Added automated lighting controls',
    'Addressed detected moisture sources',
    'Provided mobility assistance device'
  ];
  
  // Generate history items (going back up to 90 days)
  for (let i = 0; i < 50; i++) {
    const createdAt = new Date(Date.now() - (Math.random() * 90) * 24 * 60 * 60 * 1000);
    const timeToResolve = Math.floor(Math.random() * 120) + 5; // 5 to 125 minutes
    const resolvedAt = new Date(createdAt.getTime() + timeToResolve * 60 * 1000);
    
    history.push({
      id: generateId('HIST'),
      alertId: generateId('ALT'),
      alertType: randomChoice(alertTypes) as SafetyAlertType,
      severity: randomChoice(severities) as SafetySeverity,
      location: randomChoice(locations),
      createdAt,
      resolvedAt,
      resolution: randomChoice(resolutions),
      timeToResolve
    });
  }
  
  // Sort by most recent first
  return history.sort((a, b) => b.resolvedAt.getTime() - a.resolvedAt.getTime());
};

// Generate notification settings
const generateNotificationSettings = (): NotificationSettings => {
  return {
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    notificationThreshold: 'MEDIUM',
    dailyDigest: true,
    digestTime: { hour: 8, minute: 0 },
    emergencyContacts: [
      {
        id: generateId('CONTACT'),
        name: 'John Smith',
        relationship: 'Son',
        phone: '555-123-4567',
        email: 'john.smith@example.com',
        notificationPreference: 'IMMEDIATE',
        notifyOnSeverity: ['HIGH', 'MEDIUM']
      },
      {
        id: generateId('CONTACT'),
        name: 'Sarah Johnson',
        relationship: 'Daughter',
        phone: '555-987-6543',
        email: 'sarah.j@example.com',
        notificationPreference: 'DAILY',
        notifyOnSeverity: ['HIGH']
      }
    ],
    notifyOnAcknowledged: false,
    notifyOnResolved: true,
    quietHours: {
      enabled: true,
      start: { hour: 22, minute: 0 },
      end: { hour: 7, minute: 0 },
      overrideForSeverity: 'HIGH'
    }
  };
};

// Generate safety stats based on alerts and history
const generateSafetyStats = (alerts: SafetyAlert[], history: AlertHistoryItem[]): SafetyStats => {
  // Count active/acknowledged/resolved alerts
  const activeAlerts = alerts.filter(a => a.status === 'ACTIVE').length;
  const acknowledgedAlerts = alerts.filter(a => a.status === 'ACKNOWLEDGED').length;
  const resolvedAlerts = alerts.filter(a => a.status === 'RESOLVED').length;
  
  // Count alerts resolved in last 24 hours
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const resolvedToday = history.filter(h => h.resolvedAt > oneDayAgo).length;
  
  // Calculate average response time
  const responseTimes = history.map(h => h.timeToResolve);
  const averageResponseTime = responseTimes.length 
    ? Math.round(responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length) 
    : 0;
  
  // Count alerts by type
  const types = ['FALL_RISK', 'WANDERING', 'ENVIRONMENTAL', 'BEHAVIORAL'] as const;
  const alertsByType = types.map(type => {
    const count = alerts.filter(a => a.type === type).length + 
                 history.filter(h => h.alertType === type).length;
    // Generate random trend for demonstration
    const trend = Math.round((Math.random() * 40) - 20); // -20% to +20%
    
    return { type, count, trend };
  });
  
  // Count alerts by location
  const locationSet = new Set<string>([
    ...alerts.map(a => a.location.room),
    ...history.map(h => h.location)
  ]);
  const locations = Array.from(locationSet);
  
  const alertsByLocation = locations.map(location => {
    const count = alerts.filter(a => a.location.room === location).length + 
                 history.filter(h => h.location === location).length;
    const trend = Math.round((Math.random() * 40) - 20); // -20% to +20%
    
    return { location, count, trend };
  });
  
  // Count alerts by time of day
  const timeOfDay = ['MORNING', 'AFTERNOON', 'EVENING', 'NIGHT'] as const;
  const alertsByTimeOfDay = timeOfDay.map(time => {
    // In a real app, we'd categorize by actual timestamp hour
    // For demo, we'll create random but realistic distribution
    let count;
    switch (time) {
      case 'MORNING':
        count = Math.floor(Math.random() * 15) + 10; // 10-25
        break;
      case 'AFTERNOON':
        count = Math.floor(Math.random() * 10) + 5; // 5-15
        break;
      case 'EVENING':
        count = Math.floor(Math.random() * 15) + 15; // 15-30
        break;
      case 'NIGHT':
        count = Math.floor(Math.random() * 20) + 20; // 20-40
        break;
    }
    
    return { timeOfDay: time, count };
  });
  
  return {
    activeAlerts,
    acknowledgedAlerts,
    resolvedAlerts,
    resolvedToday,
    totalAlerts: alerts.length + history.length,
    averageResponseTime,
    currentRiskLevel: activeAlerts > 10 ? 'HIGH' : activeAlerts > 5 ? 'MEDIUM' : 'LOW',
    riskTrend: randomChoice(['INCREASING', 'DECREASING', 'STABLE']),
    alertsByType,
    alertsByLocation,
    alertsByTimeOfDay,
    implementedRecommendations: 12, // Placeholder for demo
    pendingHighPriorityRecommendations: 3 // Placeholder for demo
  };
};

// Main function to generate all data
export const generateSafetyData = (alertCount: number = 20) => {
  const alerts = generateSafetyAlerts(alertCount);
  const riskZones = generateRiskZones();
  const recommendations = generateSafetyRecommendations();
  const alertHistory = generateAlertHistory();
  const notificationSettings = generateNotificationSettings();
  const stats = generateSafetyStats(alerts, alertHistory);
  
  return {
    alerts,
    riskZones,
    recommendations,
    alertHistory,
    notificationSettings,
    stats
  };
}; 